"use client"

import React, { useState, useEffect } from 'react';
import { FormattedMessage } from "react-intl"
import { motion, AnimatePresence } from "framer-motion"
import apiClient from "../../utils/apiClient"

const ProfileModal = ({ isOpen, onClose }) => {
  const [userData, setUserData] = useState({
    name: "",
    email: "",
    username: "",
    joinedDate: "",
    projectsCount: 0,
    firstProjectDate: null,
    dateOfBirth: "",
    gender: "",
    country: "",
  })
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)
  const [activeTab, setActiveTab] = useState("overview")

  useEffect(() => {
    const fetchUserProfile = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const userId = localStorage.getItem("userId");
        if (!userId) {
          throw new Error("User ID not found");
        }

        const response = await apiClient.get(`/api/users/${userId}`);
        console.log('Profile response:', response.data);

        setUserData({
          ...response.data,
          firstProjectDate: response.data.firstProjectDate
        });
      } catch (error) {
        console.error("Profile loading error:", error);
        setError("Failed to load profile data");
      } finally {
        setIsLoading(false);
      }
    };

    if (isOpen) {
      fetchUserProfile();
    }
  }, [isOpen])

  if (!isOpen) return null

  const formatDate = (dateString) => {
    if (!dateString) return "Not specified"
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    })
  }

  const getInitials = (name) => {
    if (!name) return "U"
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-2 sm:p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={(e) => {
            if (e.target === e.currentTarget) onClose()
          }}
        >
          <motion.div
            className="bg-white dark:bg-gray-900 rounded-xl w-full max-w-2xl shadow-2xl overflow-hidden max-h-[95vh] sm:max-h-[90vh] flex flex-col"
            initial={{ scale: 0.9, y: 20 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.9, y: 20 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
          >
            {/* Header - Fixed */}
            <div className="bg-gradient-to-r from-blue-600 to-violet-600 px-4 sm:px-6 py-3 sm:py-4 flex justify-between items-center flex-shrink-0">
              <h2 className="text-lg sm:text-xl md:text-2xl font-bold text-white">
                <FormattedMessage
                  defaultMessage="My Profile"
                  description="Profile modal header"
                  id="gui.profileModal.header"
                />
              </h2>
              <button
                onClick={onClose}
                className="text-white bg-white/10 hover:bg-white/20 transition-colors rounded-full p-1"
                aria-label="Close"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6"
                >
                  <path d="M18 6 6 18"></path>
                  <path d="m6 6 12 12"></path>
                </svg>
              </button>
            </div>

            {/* Content - Scrollable */}
            <div className="flex-1 overflow-y-auto bg-white dark:bg-gray-900">
              {isLoading ? (
                <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
                  <div className="flex items-center space-x-4">
                    <div className="w-16 h-16 sm:w-20 sm:h-20 md:w-24 md:h-24 rounded-full bg-gray-200 animate-pulse" />
                    <div className="space-y-2">
                      <div className="h-5 sm:h-6 md:h-8 w-32 md:w-48 bg-gray-200 animate-pulse rounded" />
                      <div className="h-3 md:h-4 w-24 md:w-32 bg-gray-200 animate-pulse rounded" />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="h-16 sm:h-20 md:h-24 rounded-lg bg-gray-200 animate-pulse" />
                    <div className="h-16 sm:h-20 md:h-24 rounded-lg bg-gray-200 animate-pulse" />
                  </div>
                  <div className="space-y-4">
                    <div className="h-4 sm:h-5 md:h-6 w-full bg-gray-200 animate-pulse rounded" />
                    <div className="h-4 sm:h-5 md:h-6 w-full bg-gray-200 animate-pulse rounded" />
                    <div className="h-4 sm:h-5 md:h-6 w-full bg-gray-200 animate-pulse rounded" />
                  </div>
                </div>
              ) : error ? (
                <div className="bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 p-4 sm:p-6 text-center rounded-lg m-4 sm:m-6">
                  <div className="font-semibold text-lg mb-2">Error Loading Profile</div>
                  <p>{error}</p>
                  <button
                    className="mt-4 px-4 py-2 border border-red-300 text-red-600 rounded-md hover:bg-red-50"
                    onClick={onClose}
                  >
                    Close
                  </button>
                </div>
              ) : (
                <div>
                  {/* Profile Header */}
                  <div className="relative">
                    <div className="h-20 sm:h-24 md:h-32 bg-gradient-to-r from-blue-500/20 to-violet-500/20 dark:from-blue-900/40 dark:to-violet-900/40"></div>
                    <div className="absolute -bottom-10 sm:-bottom-12 md:-bottom-16 left-3 sm:left-4 md:left-6 w-20 h-20 sm:w-24 sm:h-24 md:w-32 md:h-32 rounded-full border-4 border-white dark:border-gray-900 shadow-lg overflow-hidden bg-gradient-to-br from-blue-500 to-violet-600">
                      <div className="w-full h-full flex items-center justify-center text-2xl sm:text-3xl md:text-4xl font-bold text-white">
                        {getInitials(userData.name)}
                      </div>
                    </div>
                  </div>

                  <div className="mt-12 sm:mt-16 md:mt-20 px-3 sm:px-4 md:px-6">
                    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-2 sm:gap-0">
                      <div>
                        <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-800 dark:text-white">
                          {userData.name}
                        </h3>
                        <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400">
                          @{userData.username || "username"}
                        </p>
                      </div>
                      <button className="px-3 py-1 text-xs sm:text-sm border border-gray-300 rounded-md bg-white text-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-300 flex items-center gap-1 w-fit">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="w-3 h-3 sm:w-4 sm:h-4 text-gray-700 dark:text-gray-300"
                        >
                          <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z"></path>
                          <path d="m15 5 4 4"></path>
                        </svg>
                        <span>Edit Profile</span>
                      </button>
                    </div>

                    <div className="mt-4 sm:mt-6">
                      <div className="flex overflow-x-auto border-b mb-4 sm:mb-6 pb-1">
                        <button
                          onClick={() => setActiveTab("overview")}
                          className={`px-2 sm:px-3 md:px-4 py-2 font-medium whitespace-nowrap text-sm sm:text-base ${activeTab === "overview" ? "border-b-2 border-blue-600 text-blue-600" : "text-gray-600 dark:text-gray-400"}`}
                        >
                          Overview
                        </button>
                        <button
                          onClick={() => setActiveTab("stats")}
                          className={`px-2 sm:px-3 md:px-4 py-2 font-medium whitespace-nowrap text-sm sm:text-base ${activeTab === "stats" ? "border-b-2 border-blue-600 text-blue-600" : "text-gray-600 dark:text-gray-400"}`}
                        >
                          Stats
                        </button>
                        <button
                          onClick={() => setActiveTab("details")}
                          className={`px-2 sm:px-3 md:px-4 py-2 font-medium whitespace-nowrap text-sm sm:text-base ${activeTab === "details" ? "border-b-2 border-blue-600 text-blue-600" : "text-gray-600 dark:text-gray-400"}`}
                        >
                          Details
                        </button>
                      </div>

                      {/* Tab Content with consistent spacing */}
                      <div className="pb-4 sm:pb-6">
                        {activeTab === "overview" && (
                          <div className="space-y-4 sm:space-y-6">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                              <div className="bg-gray-50 dark:bg-gray-800 p-3 sm:p-4 rounded-lg transition-all hover:shadow-md">
                                <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400 mb-1">
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="24"
                                    height="24"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    className="w-3 h-3 sm:w-4 sm:h-4"
                                  >
                                    <rect width="20" height="14" x="2" y="7" rx="2" ry="2"></rect>
                                    <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"></path>
                                  </svg>
                                  <span className="text-xs sm:text-sm font-medium">Projects</span>
                                </div>
                                <p className="text-xl sm:text-2xl font-bold text-blue-600 dark:text-blue-400">
                                  {userData.projectsCount}
                                </p>
                              </div>
                              <div className="bg-gray-50 dark:bg-gray-800 p-3 sm:p-4 rounded-lg transition-all hover:shadow-md">
                                <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400 mb-1">
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="24"
                                    height="24"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    className="w-3 h-3 sm:w-4 sm:h-4"
                                  >
                                    <rect width="18" height="18" x="3" y="4" rx="2" ry="2"></rect>
                                    <line x1="16" x2="16" y1="2" y2="6"></line>
                                    <line x1="8" x2="8" y1="2" y2="6"></line>
                                    <line x1="3" x2="21" y1="10" y2="10"></line>
                                  </svg>
                                  <span className="text-xs sm:text-sm font-medium">Joined</span>
                                </div>
                                <p className="text-xl sm:text-2xl font-bold text-blue-600 dark:text-blue-400">
                                  {formatDate(userData.joinedDate).split(" ")[0]}
                                </p>
                              </div>
                            </div>

                            <div className="space-y-3 sm:space-y-4 bg-gray-50 dark:bg-gray-800 p-3 sm:p-4 rounded-lg">
                              <div className="flex items-center gap-2">
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  width="24"
                                  height="24"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  className="w-4 h-4 sm:w-5 sm:h-5 text-gray-500 dark:text-gray-400 flex-shrink-0"
                                >
                                  <rect width="20" height="16" x="2" y="4" rx="2"></rect>
                                  <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"></path>
                                </svg>
                                <div className="min-w-0">
                                  <p className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400">
                                    Email
                                  </p>
                                  <p className="text-sm sm:text-base text-gray-800 dark:text-gray-200 truncate">
                                    {userData.email}
                                  </p>
                                </div>
                              </div>

                              <div className="flex items-center gap-2">
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  width="24"
                                  height="24"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  className="w-4 h-4 sm:w-5 sm:h-5 text-gray-500 dark:text-gray-400 flex-shrink-0"
                                >
                                  <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"></path>
                                  <circle cx="12" cy="10" r="3"></circle>
                                </svg>
                                <div className="min-w-0">
                                  <p className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400">
                                    Location
                                  </p>
                                  <p className="text-sm sm:text-base text-gray-800 dark:text-gray-200 truncate">
                                    {userData.country || "Not specified"}
                                  </p>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}

                        {activeTab === "stats" && (
                          <div className="space-y-4 sm:space-y-6">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                              <div className="bg-gradient-to-br from-blue-50 to-violet-50 dark:from-blue-900/20 dark:to-violet-900/20 p-4 sm:p-6 rounded-lg text-center transition-all hover:shadow-md">
                                <div className="text-2xl sm:text-3xl md:text-4xl font-bold text-blue-600 dark:text-blue-400 mb-2">
                                  {userData.projectsCount}
                                </div>
                                <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400">Total Projects</p>
                              </div>
                              <div className="bg-gradient-to-br from-blue-50 to-violet-50 dark:from-blue-900/20 dark:to-violet-900/20 p-4 sm:p-6 rounded-lg text-center transition-all hover:shadow-md">
                                <div className="text-2xl sm:text-3xl md:text-4xl font-bold text-blue-600 dark:text-blue-400 mb-2">
                                  {Math.floor((new Date() - new Date(userData.joinedDate)) / (1000 * 60 * 60 * 24))}
                                </div>
                                <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400">Days as Member</p>
                              </div>
                            </div>

                            <div className="bg-gray-50 dark:bg-gray-800 p-3 sm:p-4 rounded-lg">
                              <h4 className="text-base sm:text-lg font-semibold text-gray-700 dark:text-gray-300 mb-3 sm:mb-4">
                                Activity Timeline
                              </h4>
                              <div className="space-y-3 sm:space-y-4">
                                <div className="flex gap-3">
                                  <div className="w-1 bg-blue-500 rounded-full flex-shrink-0 h-12 sm:h-auto"></div>
                                  <div className="min-w-0">
                                    <p className="text-sm sm:text-base font-medium text-gray-800 dark:text-gray-200">
                                      Joined the platform
                                    </p>
                                    <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 truncate">
                                      {formatDate(userData.joinedDate)}
                                    </p>
                                  </div>
                                </div>
                                <div className="flex gap-3">
                                  <div className="w-1 bg-green-500 rounded-full flex-shrink-0 h-12 sm:h-auto"></div>
                                  <div className="min-w-0">
                                    <p className="text-sm sm:text-base font-medium text-gray-800 dark:text-gray-200">
                                      Created first project
                                    </p>
                                    <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 truncate">
                                      {userData.firstProjectDate 
                                        ? formatDate(userData.firstProjectDate)
                                        : "No projects yet"}
                                    </p>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}

                        {activeTab === "details" && (
                          <div className="space-y-4 sm:space-y-6">
                            <div className="bg-gray-50 dark:bg-gray-800 p-3 sm:p-4 md:p-6 rounded-lg">
                              <h4 className="text-base sm:text-lg font-semibold text-gray-700 dark:text-gray-300 mb-3 sm:mb-4">
                                Personal Information
                              </h4>
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 md:gap-6">
                                <div>
                                  <p className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400">
                                    Full Name
                                  </p>
                                  <p className="text-sm sm:text-base text-gray-800 dark:text-gray-200 truncate">
                                    {userData.name}
                                  </p>
                                </div>
                                <div>
                                  <p className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400">
                                    Username
                                  </p>
                                  <p className="text-sm sm:text-base text-gray-800 dark:text-gray-200 truncate">
                                    @{userData.username || "username"}
                                  </p>
                                </div>
                                <div>
                                  <p className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400">
                                    Date of Birth
                                  </p>
                                  <p className="text-sm sm:text-base text-gray-800 dark:text-gray-200 truncate">
                                    {formatDate(userData.dateOfBirth)}
                                  </p>
                                </div>
                                <div>
                                  <p className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400">
                                    Gender
                                  </p>
                                  <p className="text-sm sm:text-base text-gray-800 dark:text-gray-200 truncate">
                                    {userData.gender
                                      ? userData.gender.charAt(0).toUpperCase() + userData.gender.slice(1)
                                      : "Not specified"}
                                  </p>
                                </div>
                                <div>
                                  <p className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400">
                                    Email
                                  </p>
                                  <p className="text-sm sm:text-base text-gray-800 dark:text-gray-200 truncate">
                                    {userData.email}
                                  </p>
                                </div>
                                <div>
                                  <p className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400">
                                    Country
                                  </p>
                                  <p className="text-sm sm:text-base text-gray-800 dark:text-gray-200 truncate">
                                    {userData.country || "Not specified"}
                                  </p>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Footer - Fixed */}
            <div className="border-t border-gray-200 dark:border-gray-800 p-3 sm:p-4 flex justify-end gap-2 flex-shrink-0 bg-white dark:bg-gray-900">
              <button
                className="px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
                onClick={onClose}
              >
                Close
              </button>
              <button className="px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium rounded-md bg-blue-600 hover:bg-blue-700 text-white">
                Save Changes
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

export default ProfileModal
